"use strict";

import React from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { tokens } from "../public/tokens.js";

const MySwal = withReactContent(Swal);

/**
 * Build HTML for the import error popup.
 * Keep it pure: only use inputs, return string.
 */
export const buildImportErrorHtml = ({ message, total, imported, failed, errors }) => {
    // Group by row + question
    const grouped = new Map();

    (errors || []).forEach((e) => {
        const key = `${e.row}__${e.question || ""}`;
        if (!grouped.has(key)) grouped.set(key, { row: e.row, question: e.question, items: [] });
        grouped.get(key).items.push(e.error);
    });

    const groups = Array.from(grouped.values()).sort((a, b) => a.row - b.row);

    const safeMsg = message || "Fix the highlighted questions and re-upload.";

    let html = `
      <div style="
        font-family: var(--globals-font-family, 'Segoe UI');
      ">
        <!-- Header -->
        <div style="
          margin-bottom: 20px;
        ">
          <div style="
            font-size: var(--typography-font-size-2xl-20px, 20px);
            font-weight: var(--typography-font-weights-semibold, 600);
            color: var(--colors-text-primary, #1d1d1d);
            margin: 0 0 8px 0;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            Import Failed
          </div>
          <div style="
            font-size: var(--typography-font-size-lg-14px, 14px);
            color: var(--colors-text-secondary, #6c757d);
            margin: 0;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            ${safeMsg}
          </div>
        </div>

        <!-- Summary Cards -->
        <div style="
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 24px;
        ">
          <!-- Total Card -->
          <div style="
            border-radius: var(--globals-border-radius, 10px);
            padding: 12px;
            text-align: center;
            background: var(--colors-neutral-grey-100, #f8f8fc);
            border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          ">
            <div style="
              font-size: var(--typography-font-size-md-12px, 12px);
              color: var(--colors-text-secondary, #6c757d);
              margin-bottom: 6px;
              line-height: var(--typography-line-heights-1, 100%);
            ">
              Total
            </div>
            <div style="
              font-size: var(--typography-font-size-2xl-20px, 20px);
              font-weight: var(--typography-font-weights-semibold, 600);
              color: var(--colors-text-primary, #1d1d1d);
              line-height: var(--typography-line-heights-1, 100%);
            ">
              ${total ?? 0}
            </div>
          </div>

          <!-- Imported Card -->
          <div style="
            border-radius: var(--globals-border-radius, 10px);
            padding: 12px;
            text-align: center;
            background: var(--colors-neutral-grey-100, #f8f8fc);
            border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          ">
            <div style="
              font-size: var(--typography-font-size-md-12px, 12px);
              color: var(--colors-text-secondary, #6c757d);
              margin-bottom: 6px;
              line-height: var(--typography-line-heights-1, 100%);
            ">
              Imported
            </div>
            <div style="
              font-size: var(--typography-font-size-2xl-20px, 20px);
              font-weight: var(--typography-font-weights-semibold, 600);
              color: var(--colors-states-success, #2ab554);
              line-height: var(--typography-line-heights-1, 100%);
            ">
              ${imported ?? 0}
            </div>
          </div>

          <!-- Failed Card -->
          <div style="
            border-radius: var(--globals-border-radius, 10px);
            padding: 12px;
            text-align: center;
            background: var(--colors-neutral-grey-100, #f8f8fc);
            border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          ">
            <div style="
              font-size: var(--typography-font-size-md-12px, 12px);
              color: var(--colors-text-secondary, #6c757d);
              margin-bottom: 6px;
              line-height: var(--typography-line-heights-1, 100%);
            ">
              Failed
            </div>
            <div style="
              font-size: var(--typography-font-size-2xl-20px, 20px);
              font-weight: var(--typography-font-weights-semibold, 600);
              color: var(--colors-states-alert, #c1232c);
              line-height: var(--typography-line-heights-1, 100%);
            ">
              ${failed ?? 0}
            </div>
          </div>
        </div>

        <!-- Error List Section -->
        <div style="
          margin-bottom: 16px;
        ">
          <div style="
            font-size: var(--typography-font-size-lg-14px, 14px);
            font-weight: var(--typography-font-weights-semibold, 600);
            color: var(--colors-text-primary, #1d1d1d);
            margin: 0 0 12px 0;
          ">
            Errors to fix
          </div>

          <div style="
            max-height: 220px;
            overflow-y: auto;
            padding-right: 4px;
          ">
    `;

    // Error Cards
    groups.forEach((g, idx) => {
        html += `
        <div style="
          border-radius: var(--globals-border-radius, 10px);
          border-left: 4px solid var(--colors-states-alert, #c1232c);
          padding: 10px 12px;
          margin-bottom: 8px;
          background: var(--colors-surfaces-container-background, #ffffff);
          border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          box-shadow: 0 1px 2px var(--colors-surfaces-shadow, #0000001a);
        ">
          <div style="
            font-size: var(--typography-font-size-lg-14px, 14px);
            font-weight: var(--typography-font-weights-semibold, 600);
            color: var(--colors-text-primary, #1d1d1d);
            margin: 0 0 4px 0;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            Row ${g.row}
          </div>
          <div style="
            font-size: var(--typography-font-size-md-12px, 12px);
            color: var(--colors-text-secondary, #6c757d);
            margin: 0 0 6px 0;
            line-height: var(--typography-line-heights-2, 110%);
            font-style: italic;
          ">
            ${g.question || "No question text"}
          </div>
          <ul style="
            margin: 0;
            padding-left: 16px;
          ">
      `;

        g.items.forEach((msg) => {
            html += `
          <li style="
            font-size: var(--typography-font-size-md-12px, 12px);
            color: var(--colors-states-alert, #c1232c);
            margin-bottom: 2px;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            ${msg}
          </li>
        `;
        });

        html += `
          </ul>
        </div>
      `;
    });

    html += `
        </div>
      </div>

      <!-- Action Hint -->
      <div style="
        font-size: var(--typography-font-size-md-12px, 12px);
        color: var(--colors-text-secondary, #6c757d);
        text-align: center;
        padding: 8px;
        background: var(--colors-neutral-grey-100, #f8f8fc);
        border-radius: var(--globals-border-radius, 10px);
        border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
      ">
        Fix the questions listed above, then re-upload the file.
      </div>
    </div>
    `;

    return html;
};


export const showImportFailurePopup = (payload) => {
    return Swal.fire({
        icon: "error",
        title: "",
        html: buildImportErrorHtml(payload),
        width: "500px",
        confirmButtonText: "Close",
        confirmButtonColor: "var(--colors-theme-button-color, #467fcf)",
        showCloseButton: true,
        customClass: {
            confirmButton: "btn btn-primary d-flex align-items-center",
            closeButton: "btn-close",
            popup: "border-radius: var(--globals-border-radius, 10px);"
        }
    });
};


export const showImportSuccessPopup = ({ total, imported }) => {
    return Swal.fire({
        icon: "success",
        title: "Import Successful",
        html: `
      <div style="
        text-align: center;
        font-family: var(--globals-font-family, 'Segoe UI');
      ">
        <div style="
          font-size: var(--typography-font-size-2xl-20px, 20px);
          font-weight: var(--typography-font-weights-semibold, 600);
          color: var(--colors-states-success, #2ab554);
          margin-bottom: 8px;
        ">
          ${imported} imported
        </div>
        <div style="
          font-size: var(--typography-font-size-md-12px, 12px);
          color: var(--colors-text-secondary, #6c757d);
          margin-bottom: 8px;
        ">
          Total in file: ${total}
        </div>
        <div style="
          font-size: var(--typography-font-size-md-12px, 12px);
          color: var(--colors-states-warning, #f68e31);
          background: var(--colors-neutral-grey-100, #f8f8fc);
          border-radius: var(--globals-border-radius, 10px);
          padding: 10px;
          border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          margin-top: 8px;
        ">
          <strong>Note:</strong> Please review imported questions
        </div>
      </div>
    `,
        width: "420px",
        timer: 3500,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: {
            popup: "border-radius: var(--globals-border-radius, 10px);"
        }
    });
};

/**
 * Builds the HTML content for the failure popup.
 */
const BulkUpdateFailureHtml = ({ message, errors }) => {
  const groups = Array.isArray(errors) ? errors : [];

  return (
    <div style={{ fontFamily: `${tokens.typography.fontFamilies["segoe-ui"]}` }}>
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            fontSize: `${tokens.typography.fontSize["2xl-20px"]}`,
            fontWeight: `${tokens.typography.fontWeights.semibold}`,
            color: `${tokens.colors.text.primary}`,
            lineHeight: `${tokens.typography.lineHeights["110"]}`,
            marginBottom: "6px",
          }}
        >
          Bulk Update Failed
        </div>

        <div
          style={{
            fontSize: `${tokens.typography.fontSize["lg-14px"]}`,
            color: `${tokens.colors.text.secondary}`,
          }}
        >
          {message || "Bulk update failed"}
        </div>
      </div>

      {groups.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              paddingRight: "6px",
            }}
          >
            {groups.map((g, index) => (
              <div
                key={index}
                style={{
                  borderRadius: `${tokens.globals["border-radius"]}`,
                  padding: "8px",
                  marginBottom:
                    index !== groups.length - 1 ? "6px" : "0px",
                  background: `${tokens.colors.surfaces["container-background"]}`,
                  border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
                }}
              >
                <div
                  style={{
                    fontSize: `${tokens.typography.fontSize["lg-14px"]}`,
                    fontWeight: `${tokens.typography.fontWeights.semibold}`,
                    color: `${tokens.colors.text.primary}`,
                    marginBottom: "6px",
                  }}
                >
                  Row {g.row}
                </div>

                <div
                  style={{
                    fontSize: `${tokens.typography.fontSize["md-12px"]}`,
                    color: `${tokens.colors.text.secondary}`,
                    marginBottom: "6px",
                  }}
                >
                  {g.student_number || ""}
                </div>

                <div
                  style={{
                    fontSize: `${tokens.typography.fontSize["md-12px"]}`,
                    color: `${tokens.colors.states.alert}`,
                  }}
                >
                  {g.error}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Builds the HTML content for the success popup.
 */
const BulkUpdateSuccessHtml = ({ updated, status, message }) => {
  return (
    <div
      style={{
        fontFamily: `${tokens.typography.fontFamilies["segoe-ui"]}`,
        textAlign: "center",
      }}
    >
      <div
        style={{
          marginBottom: "16px",
          fontSize: `${tokens.typography.fontSize["2xl-20px"]}`,
          fontWeight: `${tokens.typography.fontWeights.semibold}`,
          color: `${tokens.colors.states.success}`,
        }}
      >
        {message || "Bulk Update Successful"}
      </div>

      <div
        style={{
          padding: "8px",
          borderRadius: `${tokens.globals["border-radius"]}`,
          background: `${tokens.colors.neutral.grey["100"]}`,
          border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
        }}
      >
        <div style={{ marginBottom: "8px" }}>
          <div
            style={{
              fontSize: `${tokens.typography.fontSize["md-12px"]}`,
              color: `${tokens.colors.text.secondary}`,
              marginBottom: "6px",
            }}
          >
            Status Updated To
          </div>

          <div
            style={{
              fontSize: `${tokens.typography.fontSize["lg-14px"]}`,
              fontWeight: `${tokens.typography.fontWeights.semibold}`,
              color: `${tokens.colors.text.primary}`,
            }}
          >
            {status}
          </div>
        </div>

        <div>
          <div
            style={{
              fontSize: `${tokens.typography.fontSize["md-12px"]}`,
              color: `${tokens.colors.text.secondary}`,
              marginBottom: "6px",
            }}
          >
            Students Updated
          </div>

          <div
            style={{
              fontSize: `${tokens.typography.fontSize["2xl-20px"]}`,
              fontWeight: `${tokens.typography.fontWeights.semibold}`,
              color: `${tokens.colors.states.success}`,
            }}
          >
            {updated}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Shows a bulk update failure popup with row-level error details.
 */
export const showBulkUpdateFailurePopup = ({ message, errors }) => {
  return MySwal.fire({
    icon: "error",
    title: "",
    html: BulkUpdateFailureHtml({ message, errors }),
    width: "500px",
    confirmButtonText: "Close",
    confirmButtonColor: `${tokens.colors.theme["button-color"]}`,
    showCloseButton: true,
    customClass: {
      confirmButton: "btn btn-primary d-flex align-items-center",
      closeButton: "btn-close",
    },
  });
};

/**
 * Shows a bulk update success popup with summary of changes applied.
 */
export const showBulkUpdateSuccessPopup = ({ updated, status, message }) => {
  return MySwal.fire({
    icon: "success",
    title: "",
    html: BulkUpdateSuccessHtml({ updated, status, message }),
    width: "500px",
    confirmButtonText: "Close",
    confirmButtonColor: `${tokens.colors.theme["button-color"]}`,
    showCloseButton: true,
    customClass: {
      confirmButton: "btn btn-primary d-flex align-items-center",
      closeButton: "btn-close",
    },
  });
};